// src/services/ezer/EzerToolExecutor.js
//
// Delegates tool execution to the backend /ezer/execute-tool endpoint.
// The backend owns all tool definitions and handles path/query/body mapping.

export class EzerToolExecutor {
  constructor(baseUrl, tokenProvider) {
    this.baseUrl = baseUrl
    this.tokenProvider = tokenProvider
  }

  /**
   * Execute a tool by name via the backend endpoint.
   *
   * Always send the chat sessionId: the backend audits every create/update/delete
   * against the logged-in user and stores this value in the log row's trackid,
   * which matches messagetracker.session_id. That lets an administrator tie an
   * audited change back to the conversation that caused it.
   *
   * @param {string} toolName
   * @param {object} toolInput
   * @param {number|string|null} locationId
   * @param {string|null} sessionId
   * @returns {Promise<{result: object, isError: boolean}>}
   */
  async execute(toolName, toolInput, locationId = null, sessionId = null) {
    try {
      const token = this.tokenProvider()
      const response = await fetch(`${this.baseUrl}/ezer/execute-tool`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tool_name: toolName,
          tool_input: toolInput,
          location_id: locationId,
          session_id: sessionId,
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        return {
          result: data.result || { error: data.message || `API error ${response.status}` },
          isError: true,
        }
      }

      return {
        result: data.result ?? data,
        isError: data.isError ?? false,
      }
    } catch (err) {
      return {
        result: { error: err.message || 'Tool execution failed' },
        isError: true,
      }
    }
  }
}
